import { useEffect, useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { usePermission } from '@/hooks/usePermission';
import { useActiveCompany } from '@/lib/active-company-context';
import { clausesOf, normCodesOf, useNormCatalog, type NormClause } from '@/lib/quality-catalogs';

import { classificationError, clauseLabel } from '../presentation';
import { useClassify } from '../use-classification';
import { Select } from '@/components/ui/Select';
import { skeletonLine } from '@/components/ui/skeleton';

// Agregar una cláusula al documento: primero la norma, luego su cláusula. Solo se
// ofrece lo que se puede elegir (cláusulas activas que el documento aún no tiene).
// Sin catálogo no hay callejón: quien administra catálogos tiene el camino a
// Catálogos; los demás saben a quién pedirlo (§E.2).

export function ClassifyModal({
  open,
  entryId,
  code,
  linkedNormIds,
  onClose,
}: {
  open: boolean;
  entryId: string;
  code: string;
  linkedNormIds: readonly string[];
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const normId = useId();
  const clauseId = useId();
  const { activeCompanyId } = useActiveCompany();
  const catalog = useNormCatalog({ companyId: activeCompanyId, enabled: open });
  const canManageCatalogs = usePermission('quality:catalogs:manage');
  const classify = useClassify(entryId);
  const { showToast } = useToast();
  const [norm, setNorm] = useState('');
  const [clause, setClause] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNorm('');
      setClause('');
      setError(null);
    }
  }, [open]);

  const available = useMemo<NormClause[]>(() => {
    const linked = new Set(linkedNormIds);
    return (catalog.data ?? []).filter((c) => c.isActive && !linked.has(c.id));
  }, [catalog.data, linkedNormIds]);
  const norms = normCodesOf(available);
  const clauses = norm === '' ? [] : clausesOf(available, norm);
  const chosen = available.find((c) => c.id === clause);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (chosen === undefined || classify.isPending) {
      return;
    }
    setError(null);
    try {
      await classify.mutateAsync(chosen.id);
      showToast(`Cláusula ${clauseLabel(chosen)} agregada a ${code}`);
      onClose();
    } catch (err) {
      setError(classificationError(err));
    }
  }

  function body(): ReactElement {
    if (catalog.isError) {
      return (
        <div className="fld-error" role="alert">
          No se pudo cargar el catálogo de normas.
          <button type="button" className="btn mut sm" onClick={() => void catalog.refetch()}>
            Reintentar
          </button>
        </div>
      );
    }
    if (catalog.data === undefined) {
      return (
        <div aria-busy="true" aria-label="Cargando normas" style={{ display: 'grid', gap: 8 }}>
          <div className={skeletonLine({ width: 70 })} />
          <div className={skeletonLine({ width: 50 })} />
        </div>
      );
    }
    if (catalog.data.every((c) => !c.isActive)) {
      return (
        <p className="modal-body" role="status">
          Todavía no hay normas activas en el catálogo de esta empresa.{' '}
          {canManageCatalogs ? (
            <Link to="/configuracion/catalogos?catalogo=normas">Ir a Catálogos</Link>
          ) : (
            'Pide al Administrador de Calidad que las agregue.'
          )}
        </p>
      );
    }
    if (norms.length === 0) {
      return (
        <p className="modal-body" role="status">
          El documento ya tiene todas las cláusulas activas del catálogo.
        </p>
      );
    }
    return (
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <div className="fld">
          <label htmlFor={normId} className="fld-lab">
            Norma
          </label>
          <Select
            id={normId}
            value={norm}
            placeholder="Selecciona una norma…"
            options={norms.map((c) => ({ value: c, label: c }))}
            onChange={(v) => {
              setNorm(v);
              setClause('');
              setError(null);
            }}
          />
        </div>
        <div className="fld">
          <label htmlFor={clauseId} className="fld-lab">
            Cláusula
          </label>
          <Select
            id={clauseId}
            value={clause}
            disabled={norm === ''}
            placeholder="Selecciona una cláusula…"
            options={clauses.map((c) => ({ value: c.id, label: `${c.clause} · ${c.title}` }))}
            onChange={(v) => {
              setClause(v);
              setError(null);
            }}
          />
        </div>
        {error !== null && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </form>
    );
  }

  return (
    <ModalShell
      open={open}
      title="Agregar cláusula"
      busy={classify.isPending}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn mut sm"
            onClick={onClose}
            disabled={classify.isPending}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            className="btn sm"
            disabled={chosen === undefined || classify.isPending}
          >
            {classify.isPending ? 'Agregando…' : 'Agregar cláusula'}
          </button>
        </>
      }
    >
      <p className="modal-body">Elige la cláusula de norma que cumple {code}.</p>
      {body()}
    </ModalShell>
  );
}
